import { useEffect, useMemo, useState } from "react";
import { getHiredEmployees } from "../../services/hrOpsApi";
import { getMergePreview, mergeEmployees } from "../../services/employeeMergeApi";
import { formatDateRu } from "../../utils/dateFormat";

/**
 * «Объединить карточки» (Чат 4, правка №15, доработка 5 — Света 04.11). [Claude] 2026-11-04
 * Выбираем вторую карточку и какая из двух основная; по каждому полю — откуда брать значение
 * (номер есть в одной и нет в другой и т. п.). Всё связанное со второй карточкой переходит
 * в основную, вторая удаляется. Только владелец платформы.
 */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

export function mergeValueText(key, value) {
    if (value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length)) return "—";
    if (key === "employment") {
        if (!value || !value.date_fired) return "работает";
        return "уволен " + formatDateRu(value.date_fired) + (value.termination_reason ? " — " + value.termination_reason : "");
    }
    if (key === "erp_user_id" || key === "user_id" || key === "candidate_id" || key === "department_contact_id") return "есть (" + String(value).slice(0, 8) + ")";
    if (typeof value === "boolean") return value ? "да" : "нет";
    if (Array.isArray(value)) return value.join(", ");
    if (typeof value === "string" && ISO_DATE.test(value)) return formatDateRu(value);
    return String(value);
}

const linksText = (links) => (links || []).map((l) => l.label + ": " + l.count).join(", ") || "нет";

export default function EmployeeMergeDialog({ employee, onClose, onMerged }) {
    const [people, setPeople] = useState([]);
    const [query, setQuery] = useState("");
    const [other, setOther] = useState(null);
    const [keepThis, setKeepThis] = useState(true);
    const [preview, setPreview] = useState(null);
    const [choices, setChoices] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        Promise.resolve().then(() => getHiredEmployees(null, { activeOnly: false }))
            .then((rows) => { if (alive) setPeople(Array.isArray(rows) ? rows : []); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    const sourceId = other ? (keepThis ? other.id : employee.id) : null;
    const targetId = other ? (keepThis ? employee.id : other.id) : null;

    useEffect(() => {
        if (!sourceId || !targetId) { setPreview(null); return undefined; }
        let alive = true;
        setError(null);
        Promise.resolve().then(() => getMergePreview(sourceId, targetId))
            .then((data) => {
                if (!alive) return;
                setPreview(data);
                setChoices(Object.fromEntries((data?.fields || []).map((f) => [f.key, f.default])));
            })
            .catch((e) => { if (alive) setError(e.message || "Не удалось загрузить карточки"); });
        return () => { alive = false; };
    }, [sourceId, targetId]);

    const found = useMemo(() => {
        const q = query.trim().toLocaleLowerCase("ru");
        return people.filter((p) => p.id !== employee.id && (!q || String(p.full_name || "").toLocaleLowerCase("ru").includes(q) || String(p.id) === q)).slice(0, 8);
    }, [people, query, employee.id]);

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            const result = await mergeEmployees({ sourceId, targetId, fields: choices });
            onMerged?.(result);
        } catch (e) {
            setError(e.message || "Не удалось объединить");
        } finally {
            setBusy(false);
        }
    };

    const main = preview?.target;
    const second = preview?.source;

    return <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4" role="dialog" aria-modal="true" data-testid="employee-merge-dialog">
        <div className="w-full max-w-4xl rounded-2xl bg-white p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">Объединить карточки</h2>
                    <p className="mt-1 text-sm text-slate-500">По каждому полю выберите, откуда брать значение. Всё связанное (контакты, родственники, документы, фото, техника, адаптация, резюме, структура) перейдёт в основную карточку, вторая будет удалена.</p>
                </div>
                <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Закрыть">✕</button>
            </div>

            {!other ? <div className="mt-4">
                <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Кого объединить с этой карточкой: ФИО или номер карточки" className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="merge-search" />
                <ul className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200">
                    {found.map((p) => <li key={p.id}><button type="button" onClick={() => setOther(p)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-amber-50" data-testid={"merge-pick-" + p.id}>
                        <span><b>{p.full_name}</b> <span className="text-slate-500">· {[p.position, p.department].filter(Boolean).join(" · ") || "—"}</span></span>
                        <span className="text-xs text-slate-400">#{p.id}{p.date_fired ? " · уволен " + formatDateRu(p.date_fired) : ""}{p.erp_user_id ? " · учётка ERP" : ""}</span>
                    </button></li>)}
                    {!found.length ? <li className="px-3 py-3 text-sm text-slate-500">Никого не нашли</li> : null}
                </ul>
            </div> : <div className="mt-4 space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                    <span>Основная карточка:</span>
                    <button type="button" onClick={() => setKeepThis(true)} className={"rounded-lg px-3 py-1.5 font-semibold " + (keepThis ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-700")} data-testid="merge-keep-this">{employee.full_name} #{employee.id}</button>
                    <button type="button" onClick={() => setKeepThis(false)} className={"rounded-lg px-3 py-1.5 font-semibold " + (!keepThis ? "bg-slate-900 text-white" : "border border-slate-200 text-slate-700")} data-testid="merge-keep-other">{other.full_name} #{other.id}</button>
                    <button type="button" onClick={() => { setOther(null); setPreview(null); }} className="ml-auto text-xs text-slate-400 hover:text-slate-700">выбрать другую</button>
                </div>
                {error ? <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{error}</p> : null}
                {!preview ? <p className="text-sm text-slate-500">Загрузка…</p> : <>
                    <div className="max-h-[55vh] overflow-auto rounded-xl border border-slate-200">
                        <table className="w-full text-sm" data-testid="merge-fields">
                            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>
                                <th className="px-3 py-2">Поле</th>
                                <th className="px-3 py-2">Основная · {main?.full_name} #{main?.id}</th>
                                <th className="px-3 py-2">Вторая · {second?.full_name} #{second?.id}</th>
                            </tr></thead>
                            <tbody className="divide-y divide-slate-100">
                                {preview.fields.map((f) => <tr key={f.key} className={f.same ? "text-slate-400" : ""}>
                                    <td className="px-3 py-1.5 font-semibold">{f.label}</td>
                                    {["target", "source"].map((side) => <td key={side} className="px-3 py-1.5">
                                        <label className="flex cursor-pointer items-start gap-2">
                                            <input type="radio" name={"merge-" + f.key} checked={choices[f.key] === side} onChange={() => setChoices((c) => ({ ...c, [f.key]: side }))} disabled={f.same} data-testid={"merge-" + f.key + "-" + side} />
                                            <span className="break-words">{mergeValueText(f.key, f[side])}</span>
                                        </label>
                                    </td>)}
                                </tr>)}
                            </tbody>
                        </table>
                    </div>
                    <p className="text-xs text-slate-500">Перейдёт в основную со второй карточки: {linksText(preview.source_links)}.</p>
                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800">Отмена</button>
                        <button type="button" disabled={busy} onClick={submit} className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-slate-900 hover:bg-amber-300 disabled:opacity-50" data-testid="merge-submit">{busy ? "Объединяем…" : "Объединить — «" + (second?.full_name || "") + "» будет удалена"}</button>
                    </div>
                </>}
            </div>}
        </div>
    </div>;
}
